/**
 * Wave 3 v1 — global referral click attributor.
 *
 * On every route change, looks for `?ref=CODE` in the current URL. If
 * present:
 *   1. POSTs to /api/referral/click to set the qt_referral_code cookie
 *      and log one click event on the backend.
 *   2. Strips the `?ref=` param from the URL so it doesn't get shared
 *      around inadvertently.
 *
 * Renders nothing — pure side-effect component. Mount once at the
 * router root.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_FLAG = "qt_ref_click_fired";   // dedupe within one tab


export default function ReferralAttribution() {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const code = (params.get("ref") || "").trim().toUpperCase();
    if (!code) return;

    // Dedupe — one click per tab per code.
    let alreadyFired = false;
    try {
      alreadyFired = sessionStorage.getItem(SESSION_FLAG) === code;
    } catch {
      // sessionStorage may throw in strict-mode iframes; just retry.
    }
    if (alreadyFired) return;

    axios.post(`${API}/api/referral/click?code=${encodeURIComponent(code)}`,
               null, { withCredentials: true })
      .then(() => {
        try { sessionStorage.setItem(SESSION_FLAG, code); } catch { /* */ }
      })
      .catch(() => { /* swallow — never break a landing page on referral */ });

    // Strip the ?ref= param so the URL stays clean. Preserve other params.
    params.delete("ref");
    const newSearch = params.toString();
    navigate(`${pathname}${newSearch ? `?${newSearch}` : ""}`,
             { replace: true });
  }, [search, pathname, navigate]);

  return null;
}
